import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { fetchWithRetry } from '@/lib/apiRequest';

export interface LegalChecklistItem {
  id: string;
  title: string;
  description: string | null;
  sortOrder: number;
  isRequired: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface LegalChecklist {
  id: string;
  name: string;
  legalArea: string;
  serviceType: string | null;
  description: string | null;
  complexity: 'low' | 'medium' | 'high';
  practicalNotes: string | null;
  tags: string;
  isPinned: boolean;
  isActive: boolean;
  itemCount: number;
  items: LegalChecklistItem[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateLegalChecklistInput {
  name: string;
  legalArea: string;
  serviceType?: string | null;
  description?: string | null;
  complexity: 'low' | 'medium' | 'high';
  practicalNotes?: string | null;
  tags?: string | null;
  isPinned?: boolean;
  isActive?: boolean;
  items?: Array<{
    title: string;
    description?: string | null;
    sortOrder?: number;
    isRequired?: boolean;
    isActive?: boolean;
  }>;
}

export type UpdateLegalChecklistInput = CreateLegalChecklistInput;

type UseLegalChecklistsOptions = {
  autoLoad?: boolean;
};

type LegalChecklistsApiResponse = {
  success?: boolean;
  legalChecklists?: unknown;
  legalChecklist?: unknown;
  error?: string;
};

const nowIso = () => new Date().toISOString();

const emptyChecklist = (): LegalChecklist[] => [];

const toNullableText = (value: unknown) => {
  if (value === undefined || value === null) {
    return null;
  }

  const text = String(value).trim();
  return text === '' ? null : text;
};

const toBoolean = (value: unknown, fallback = false) => {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value !== 0;
  }

  const normalized = String(value).trim().toLowerCase();
  if (['0', 'false', 'inactive', 'inativo', 'archived', 'arquivado', 'nao', 'não'].includes(normalized)) {
    return false;
  }

  return true;
};

const normalizeComplexity = (value: unknown): LegalChecklist['complexity'] => {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === 'low' || normalized === 'medium' || normalized === 'high') {
    return normalized;
  }
  return 'medium';
};

const normalizeChecklist = (value: any): LegalChecklist => ({
  id: String(value?.id ?? ''),
  name: String(value?.name ?? '').trim(),
  legalArea: String(value?.legalArea ?? value?.legal_area ?? '').trim(),
  serviceType: toNullableText(value?.serviceType ?? value?.service_type),
  description: toNullableText(value?.description),
  complexity: normalizeComplexity(value?.complexity),
  practicalNotes: toNullableText(value?.practicalNotes ?? value?.practical_notes),
  tags: String(value?.tags ?? '').trim(),
  isPinned: toBoolean(value?.isPinned ?? value?.is_pinned),
  isActive: toBoolean(value?.isActive ?? value?.is_active, true),
  itemCount: Number(value?.itemCount ?? value?.item_count ?? (Array.isArray(value?.items) ? value.items.length : 0)) || 0,
  items: Array.isArray(value?.items)
    ? value.items.map((item: any) => ({
        id: String(item?.id ?? ''),
        title: String(item?.title ?? '').trim(),
        description: toNullableText(item?.description),
        sortOrder: Number(item?.sortOrder ?? item?.sort_order ?? 0) || 0,
        isRequired: toBoolean(item?.isRequired ?? item?.is_required),
        isActive: toBoolean(item?.isActive ?? item?.is_active, true),
        createdAt: toNullableText(item?.createdAt ?? item?.created_at) ?? nowIso(),
        updatedAt: toNullableText(item?.updatedAt ?? item?.updated_at) ?? nowIso(),
      }))
    : [],
  createdAt: toNullableText(value?.createdAt ?? value?.created_at) ?? nowIso(),
  updatedAt: toNullableText(value?.updatedAt ?? value?.updated_at) ?? nowIso(),
});

const sortChecklists = (a: LegalChecklist, b: LegalChecklist) =>
  Number(b.isPinned) - Number(a.isPinned) ||
  a.legalArea.localeCompare(b.legalArea, 'pt-BR') ||
  a.name.localeCompare(b.name, 'pt-BR');

const sortAndSetChecklists = (setLegalChecklists: Dispatch<SetStateAction<LegalChecklist[]>>, next: LegalChecklist[]) => {
  setLegalChecklists([...next].sort(sortChecklists));
};

const buildPayload = (input: CreateLegalChecklistInput): CreateLegalChecklistInput => ({
  name: String(input.name ?? '').trim(),
  legalArea: String(input.legalArea ?? '').trim(),
  serviceType: toNullableText(input.serviceType),
  description: toNullableText(input.description),
  complexity: normalizeComplexity(input.complexity),
  practicalNotes: toNullableText(input.practicalNotes),
  tags: toNullableText(input.tags),
  isPinned: Boolean(input.isPinned),
  isActive: input.isActive === undefined ? true : Boolean(input.isActive),
  items: Array.isArray(input.items)
    ? input.items
        .map((item, index) => ({
          title: String(item?.title ?? '').trim(),
          description: toNullableText(item?.description),
          sortOrder: Number.isFinite(Number(item?.sortOrder)) ? Number(item?.sortOrder) : index + 1,
          isRequired: Boolean(item?.isRequired),
          isActive: item?.isActive === undefined ? true : Boolean(item?.isActive),
        }))
        .filter((item) => item.title !== '')
    : [],
});

type ChecklistApiPayload = {
  success?: boolean;
  legalChecklist?: unknown;
  error?: string;
};

export function useLegalChecklists(options: UseLegalChecklistsOptions = {}) {
  const { autoLoad = true } = options;
  const [legalChecklists, setLegalChecklists] = useState<LegalChecklist[]>([]);
  const [isLoadingLegalChecklists, setIsLoadingLegalChecklists] = useState(true);
  const [isSavingLegalChecklists, setIsSavingLegalChecklists] = useState(false);
  const [legalChecklistsError, setLegalChecklistsError] = useState<string | null>(null);
  const [legalChecklistsSuccess, setLegalChecklistsSuccess] = useState<string | null>(null);

  const loadLegalChecklists = useCallback(async (options?: { includeArchived?: boolean }) => {
    setIsLoadingLegalChecklists(true);
    setLegalChecklistsError(null);

    try {
      const searchParams = new URLSearchParams();
      if (options?.includeArchived) {
        searchParams.set('includeArchived', '1');
      }

      const query = searchParams.toString();
      const response = await fetch(`/api/legal-checklists${query ? `?${query}` : ''}`);
      const data = (await response.json()) as LegalChecklistsApiResponse;

      if (!response.ok || !data.success || !Array.isArray(data.legalChecklists)) {
        throw new Error(data.error || `GET /api/legal-checklists falhou com status ${response.status}`);
      }

      sortAndSetChecklists(setLegalChecklists, data.legalChecklists.map(normalizeChecklist));
    } catch (error) {
      console.error('Erro ao carregar checklists jurídicos.', error);
      setLegalChecklists(emptyChecklist());
      setLegalChecklistsError('Não foi possível carregar os checklists jurídicos.');
    } finally {
      setIsLoadingLegalChecklists(false);
    }
  }, []);

  const createLegalChecklist = useCallback(async (input: CreateLegalChecklistInput) => {
    setIsSavingLegalChecklists(true);
    setLegalChecklistsError(null);
    setLegalChecklistsSuccess(null);

    try {
      const response = await fetchWithRetry('/api/legal-checklists', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(buildPayload(input)),
      });

      const data = (await response.json()) as ChecklistApiPayload;

      if (!response.ok || !data.success || !data.legalChecklist) {
        throw new Error(data.error || `POST /api/legal-checklists falhou com status ${response.status}`);
      }

      const created = normalizeChecklist(data.legalChecklist);
      setLegalChecklists((prev) => [created, ...prev.filter((item) => item.id !== created.id)].sort(sortChecklists));
      setLegalChecklistsSuccess('Checklist criado com sucesso.');
      return created;
    } catch (error: any) {
      const message = error?.message || 'Não foi possível criar o checklist.';
      setLegalChecklistsError(message);
      throw error;
    } finally {
      setIsSavingLegalChecklists(false);
    }
  }, []);

  const updateLegalChecklist = useCallback(async (id: string, input: UpdateLegalChecklistInput) => {
    setIsSavingLegalChecklists(true);
    setLegalChecklistsError(null);
    setLegalChecklistsSuccess(null);

    try {
      const response = await fetch(`/api/legal-checklists/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(buildPayload(input)),
      });

      const data = (await response.json()) as ChecklistApiPayload;

      if (!response.ok || !data.success || !data.legalChecklist) {
        throw new Error(data.error || `PUT /api/legal-checklists/${id} falhou com status ${response.status}`);
      }

      const updated = normalizeChecklist(data.legalChecklist);
      setLegalChecklists((prev) => [updated, ...prev.filter((item) => item.id !== updated.id)].sort(sortChecklists));
      setLegalChecklistsSuccess('Checklist atualizado com sucesso.');
      return updated;
    } catch (error: any) {
      const message = error?.message || 'Não foi possível atualizar o checklist.';
      setLegalChecklistsError(message);
      throw error;
    } finally {
      setIsSavingLegalChecklists(false);
    }
  }, []);

  const archiveLegalChecklist = useCallback(async (id: string) => {
    setIsSavingLegalChecklists(true);
    setLegalChecklistsError(null);
    setLegalChecklistsSuccess(null);

    try {
      const response = await fetch(`/api/legal-checklists/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      });

      const data = (await response.json()) as ChecklistApiPayload;

      if (!response.ok || !data.success) {
        throw new Error(data.error || `DELETE /api/legal-checklists/${id} falhou com status ${response.status}`);
      }

      setLegalChecklists((prev) => prev.filter((item) => item.id !== id));
      setLegalChecklistsSuccess('Checklist arquivado com sucesso.');
    } catch (error: any) {
      const message = error?.message || 'Não foi possível arquivar o checklist.';
      setLegalChecklistsError(message);
      throw error;
    } finally {
      setIsSavingLegalChecklists(false);
    }
  }, []);

  const togglePinLegalChecklist = useCallback(
    async (id: string) => {
      const checklist = legalChecklists.find((item) => item.id === id);
      if (!checklist) {
        throw new Error('Checklist não encontrado.');
      }

      return updateLegalChecklist(id, {
        name: checklist.name,
        legalArea: checklist.legalArea,
        serviceType: checklist.serviceType,
        description: checklist.description,
        complexity: checklist.complexity,
        practicalNotes: checklist.practicalNotes,
        tags: checklist.tags,
        isPinned: !checklist.isPinned,
        isActive: checklist.isActive,
        items: checklist.items.map((item) => ({
          title: item.title,
          description: item.description,
          sortOrder: item.sortOrder,
          isRequired: item.isRequired,
          isActive: item.isActive,
        })),
      });
    },
    [legalChecklists, updateLegalChecklist]
  );

  const refreshLegalChecklists = useCallback(async () => {
    await loadLegalChecklists();
  }, [loadLegalChecklists]);

  const clearLegalChecklistsFeedback = useCallback(() => {
    setLegalChecklistsError(null);
    setLegalChecklistsSuccess(null);
  }, []);

  useEffect(() => {
    if (!autoLoad) {
      setIsLoadingLegalChecklists(false);
      return;
    }

    void loadLegalChecklists();
  }, [autoLoad, loadLegalChecklists]);

  return {
    legalChecklists,
    isLoadingLegalChecklists,
    isSavingLegalChecklists,
    legalChecklistsError,
    legalChecklistsSuccess,
    loadLegalChecklists,
    createLegalChecklist,
    updateLegalChecklist,
    archiveLegalChecklist,
    togglePinLegalChecklist,
    refreshLegalChecklists,
    clearLegalChecklistsFeedback,
    setLegalChecklists,
  };
}
